import { useEffect, useState } from "react";

const prefersReducedMotion = () =>
    typeof window !== "undefined" &&
    window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

/**
 * Animates a number from 0 up to `target` on a rAF loop.
 *
 * rAF rather than a CSS transition because the value is text, not a style.
 * Eases out so the number decelerates into place instead of stopping dead, and
 * snaps straight to the target when the user has asked for reduced motion.
 *
 * @param {number|null} target  null while the value is still loading
 * @param {number} duration     ms
 */
export default function useCountUp(target, duration = 900) {
    const [value, setValue] = useState(0);

    useEffect(() => {
        if (target === null || target === undefined) return undefined;
        if (prefersReducedMotion() || duration <= 0) {
            setValue(target);
            return undefined;
        }

        let frame;
        let startedAt;

        const tick = (now) => {
            if (startedAt === undefined) startedAt = now;
            const progress = Math.min((now - startedAt) / duration, 1);
            const eased = 1 - Math.pow(1 - progress, 3); // easeOutCubic
            setValue(target * eased);
            if (progress < 1) frame = requestAnimationFrame(tick);
        };

        frame = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(frame);
    }, [target, duration]);

    return value;
}
